import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, CheckCircle, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

interface Profile {
  id: string;
  username: string | null;
  bio: string | null;
}

interface MentorMatch {
  id: string;
  mentor_id: string;
  mentee_id: string;
  status: string;
  message: string | null;
  created_at: string;
  mentor_profile?: Profile;
  mentee_profile?: Profile;
}

export const MentorshipMatching = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [myMatches, setMyMatches] = useState<MentorMatch[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<Profile | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load potential mentors
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("*")
      .neq("id", session.user.id)
      .limit(10);

    setProfiles(profilesData || []);

    // Load my matches
    const { data: matchesData } = await supabase
      .from("mentor_matches")
      .select("*")
      .or(`mentee_id.eq.${session.user.id},mentor_id.eq.${session.user.id}`);

    if (matchesData) {
      const userIds = [...new Set([
        ...matchesData.map(m => m.mentor_id),
        ...matchesData.map(m => m.mentee_id)
      ])];

      const { data: profilesData } = await supabase
        .from("profiles")
        .select("id, username, bio")
        .in("id", userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      const enrichedMatches = matchesData.map(match => ({
        ...match,
        mentor_profile: profileMap.get(match.mentor_id),
        mentee_profile: profileMap.get(match.mentee_id)
      }));

      setMyMatches(enrichedMatches);
    }

    setLoading(false);
  };

  const requestMentorship = async () => {
    if (!selectedMentor) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Please sign in to request mentorship");
      return;
    }

    const { error } = await supabase
      .from("mentor_matches")
      .insert({
        mentee_id: session.user.id,
        mentor_id: selectedMentor.id,
        message,
        status: "pending"
      });

    if (error) {
      toast.error("Failed to send request");
    } else {
      toast.success("Mentorship request sent!");
      setMessage("");
      setDialogOpen(false);
      loadData();
      
      // Track activity
      await supabase.from("activity_feed").insert({
        user_id: session.user.id,
        activity_type: "mentor_match",
        metadata: { mentor_id: selectedMentor.id }
      });
    }
  };

  const updateMatchStatus = async (matchId: string, status: string) => {
    const { error } = await supabase
      .from("mentor_matches")
      .update({ status })
      .eq("id", matchId);

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success(`Request ${status}`);
      loadData();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Mentorship Program</h2>
        <p className="text-muted-foreground">Connect with experienced members for guidance</p>
      </div>

      {/* My Matches */}
      {myMatches.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold">My Connections</h3>
          {myMatches.map((match) => (
            <Card key={match.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {match.mentor_profile?.username || "Anonymous"}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {match.status === "pending" && <Clock className="h-4 w-4 text-yellow-500" />}
                    {match.status === "accepted" && <CheckCircle className="h-4 w-4 text-green-500" />}
                    {match.status === "declined" && <XCircle className="h-4 w-4 text-red-500" />}
                    <span className="text-sm capitalize">{match.status}</span>
                  </div>
                </div>
                {match.message && (
                  <CardDescription>{match.message}</CardDescription>
                )}
              </CardHeader>
              {match.status === "pending" && (
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => updateMatchStatus(match.id, "accepted")}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateMatchStatus(match.id, "declined")}
                    >
                      Decline
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Find Mentors */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Find a Mentor</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {profiles.map((profile) => (
            <Card key={profile.id} className="hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {profile.username || "Anonymous"}
                </CardTitle>
                {profile.bio && (
                  <CardDescription className="line-clamp-2">{profile.bio}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <Dialog open={dialogOpen && selectedMentor?.id === profile.id} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setSelectedMentor(profile)}
                    >
                      Request Mentorship
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Request Mentorship from {profile.username}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Introduce yourself and explain why you'd like this person as a mentor..."
                        rows={6}
                      />
                      <Button onClick={requestMentorship} className="w-full">
                        Send Request
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};